import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ServerStackIcon } from '@heroicons/react/24/outline';
import { discordApi } from '../services/api';
import { toast } from 'react-hot-toast';

interface DiscordServer {
  id: string;
  name: string;
  icon: string | null;
  owner: boolean;
  permissions: number;
}

interface DiscordServerModalProps {
  isOpen: boolean;
  onClose: () => void;
  chatbotId: string;
  chatbotName: string;
  onSuccess: () => void;
}

export default function DiscordServerModal({ 
  isOpen, 
  onClose, 
  chatbotId, 
  chatbotName, 
  onSuccess 
}: DiscordServerModalProps) {
  const [servers, setServers] = useState<DiscordServer[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedServerId, setSelectedServerId] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      loadDiscordServers();
    }
  }, [isOpen]);

  const loadDiscordServers = async () => {
    try {
      setLoading(true);
      const response = await discordApi.getDiscordServers();
      
      if (response.success && response.data) {
        setServers(response.data.servers);
      } else {
        toast.error('Failed to load Discord servers');
      }
    } catch (error: any) {
      console.error('Error loading Discord servers:', error);
      
      if (error.response?.status === 400) {
        const errorMessage = error.response?.data?.error || 'Discord account not connected. Please link your Discord account first.';
        toast.error(errorMessage);
      } else {
        toast.error('Failed to load Discord servers');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedServerId) {
      toast.error('Please select a Discord server');
      return;
    }

    const selectedServer = servers.find(s => s.id === selectedServerId);
    if (!selectedServer) {
      toast.error('Selected server not found');
      return;
    }

    try {
      setSubmitting(true);
      
      const response = await discordApi.createDiscordIntegration(
        selectedServerId,
        chatbotId,
        selectedServer.name
      );

      if (response.success) {
        toast.success(`Successfully integrated with Discord server "${selectedServer.name}"`);
        onSuccess();
        onClose();
      } else {
        toast.error('Failed to create Discord integration');
      }
    } catch (error: any) {
      console.error('Error creating Discord integration:', error);
      
      const errorMessage = error.response?.data?.error || 'Failed to create Discord integration';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedServerId('');
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100"
                  >
                    Connect to Discord Server
                  </Dialog.Title>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    onClick={handleClose}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="mb-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Select a Discord server where you want to deploy the chatbot "<strong>{chatbotName}</strong>".
                    You must have admin permissions in the server.
                  </p>
                </div>

                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                    <span className="ml-3 text-sm text-gray-600 dark:text-gray-400">
                      Loading your Discord servers...
                    </span>
                  </div>
                ) : servers.length === 0 ? (
                  <div className="text-center py-8">
                    <ServerStackIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                      No Discord servers found
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      You need admin permissions in at least one Discord server to create integrations.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Choose Discord Server
                      </label>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {servers.map((server) => (
                          <label
                            key={server.id}
                            className="flex items-center p-3 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            <input
                              type="radio"
                              name="server"
                              value={server.id}
                              checked={selectedServerId === server.id}
                              onChange={(e) => setSelectedServerId(e.target.value)}
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600"
                            />
                            <div className="ml-3 flex items-center">
                              {server.icon ? (
                                <img
                                  src={`https://cdn.discordapp.com/icons/${server.id}/${server.icon}.png`}
                                  alt={server.name}
                                  className="w-8 h-8 rounded-full mr-3"
                                />
                              ) : (
                                <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center mr-3">
                                  <span className="text-white text-sm font-medium">
                                    {server.name.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                              )}
                              <div>
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {server.name}
                                </p>
                                {server.owner && (
                                  <p className="text-xs text-green-600 dark:text-green-400">
                                    Owner
                                  </p>
                                )}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={handleClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={!selectedServerId || submitting}
                        className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        {submitting ? (
                          <div className="flex items-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Connecting...
                          </div>
                        ) : (
                          'Connect to Server'
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}